import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Text,
  Stack,
  Code,
  LoadingOverlay,
  Switch,
  TextInput,
  Textarea,
  Grid,
  Group,
  Divider,
  Select,
  Paper,
} from "@mantine/core";
import "@mantine/dropzone/styles.css";
import * as XLSX from "xlsx";
import { Dropzone } from "@mantine/dropzone";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import {
  PostChecklistMcTemplatesPublishApiArg,
  usePostChecklistMcTemplatesPublishMutation,
  useGetChecklistByChecklistIdExportQuery,
  GetChecklistByChecklistIdExportApiResponse,
} from "../../openapi";

const statusOptions = ["ACTIVE", "IN_DEVELOPMENT", "REMOVED"] as const;

// 🔹 Zod: endast formatvalidering av e-post
const metadataSchema = z.object({
  name: z.string().min(1, "Navn er påkrevd"),
  status: z.enum(statusOptions, { required_error: "Status er påkrevd" }),
  disciplineCode: z.string().optional(),
  templateDescription: z.string().optional(),
  subDiscipline: z.string().optional(),
  competenceNetwork: z.string().optional(),
  documentManagers: z
    .object({
      preparedBy: z.string().optional(),
      checkedBy: z.string().optional(),
      approvedBy: z.string().optional(),
    })
    .optional(),
  disciplineLeader: z.string().optional(),
});

type MetadataForm = z.infer<typeof metadataSchema>;
type ApiMetadata = PostChecklistMcTemplatesPublishApiArg["body"]["metadata"];

const initialMetadata: MetadataForm = {
  name: "",
  status: "IN_DEVELOPMENT",
  disciplineCode: "",
  templateDescription: "",
  subDiscipline: "",
  competenceNetwork: "",
  documentManagers: {
    preparedBy: "",
    checkedBy: "",
    approvedBy: "",
  },
  disciplineLeader: "",
};

// Map export → form (status finnes ikke i export – behold default)
function mapExportToForm(e: GetChecklistByChecklistIdExportApiResponse): Partial<MetadataForm> {
  return {
    name: e.name ?? "",
    disciplineCode: e.disciplineCode ?? "",
    templateDescription: e.templateDescription ?? "",
    subDiscipline: e.subDiscipline ?? "",
    competenceNetwork: e.competenceNetwork ?? "",
    documentManagers: {
      preparedBy: e.documentManagers?.preparedBy ?? "",
      checkedBy: e.documentManagers?.checkedBy ?? "",
      approvedBy: e.documentManagers?.approvedBy ?? "",
    },
    disciplineLeader: e.disciplineLeader ?? "",
  };
}

const PublishChecklistModal: React.FC<{ checklistNrId: number; onClose: () => void }> = ({
  checklistNrId,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);

  const {
    data: exportData,
    isLoading: isLoadingExport,
    isError: isErrorExport,
    error: exportError,
  } = useGetChecklistByChecklistIdExportQuery({ checklistId: checklistNrId, excel: false }, { skip: !checklistNrId });

  const form = useForm<MetadataForm>({
    initialValues: initialMetadata,
    validate: zodResolver(metadataSchema),
    validateInputOnBlur: true,
  });

  const [postChecklistPublish, { isLoading, error, data, reset }] = usePostChecklistMcTemplatesPublishMutation();

  useEffect(() => {
    setFile(null);
    setDryRun(false);
    form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, reset]);

  useEffect(() => {
    if (exportData) {
      form.setValues((prev) => ({
        ...prev,
        ...mapExportToForm(exportData),
        status: prev.status ?? "IN_DEVELOPMENT",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleDrop = (files: File[]) => setFile(files[0]);

  const handleSubmit = async () => {
    const valid = form.validate();
    if (valid.hasErrors || !file) return;

    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const base64String = XLSX.write(workbook, { type: "base64", bookType: "xlsx" });

    // Normaliser e-post (trim + lowercase) og fjern tomme felt
    const dm = form.values.documentManagers ?? {};
    const norm = (v?: string) => (v && v.trim() ? v.trim().toLowerCase() : "");
    const cleanedDM: ApiMetadata["documentManagers"] = {
      ...(norm(dm.preparedBy) ? { preparedBy: norm(dm.preparedBy) } : {}),
      ...(norm(dm.checkedBy) ? { checkedBy: norm(dm.checkedBy) } : {}),
      ...(norm(dm.approvedBy) ? { approvedBy: norm(dm.approvedBy) } : {}),
    };
    const dmOrUndefined = Object.keys(cleanedDM).length ? cleanedDM : undefined;

    const disciplineLeader = norm(form.values.disciplineLeader) || undefined;

    const metadataPayload: ApiMetadata = {
      name: form.values.name,
      status: form.values.status,
      disciplineCode: form.values.disciplineCode || undefined,
      templateDescription: form.values.templateDescription || undefined,
      subDiscipline: form.values.subDiscipline || undefined,
      competenceNetwork: form.values.competenceNetwork || undefined,
      documentManagers: dmOrUndefined,
      disciplineLeader,
    };

    postChecklistPublish({
      body: {
        checklistNrId,
        file: base64String,
        dryRun,
        metadata: metadataPayload,
      },
    });
  };

  const renderValidationResult = () => {
    if (isLoading) return <LoadingOverlay visible />;
    if (error)
      return (
        <Stack>
          <Text fw={500}>Publiseringsresultat (feil):</Text>
          <Code
            block
            style={{
              backgroundColor: "#ffcccc",
              color: "#800000",
              padding: "16px",
              borderRadius: "4px",
              border: "1px solid #ff0000",
            }}
          >
            {JSON.stringify(error, null, 2)}
          </Code>
        </Stack>
      );

    if (data && "status" in data) {
      let backgroundColor = "#f0f0f0";
      let color = "#333";
      let borderColor = "#ccc";
      if (data.status === "error") {
        backgroundColor = "#ffcccc";
        color = "#800000";
        borderColor = "#ff0000";
      } else if (data.status === "success") {
        backgroundColor = "#ccffcc";
        color = "#006600";
        borderColor = "#00cc00";
      } else if (data.status === "dry-run") {
        backgroundColor = "#f0f0f0";
        color = "#333";
        borderColor = "#ccc";
      }
      return (
        <Stack>
          <Text fw={500}>Publiseringsresultat:</Text>
          <Code
            block
            style={{
              backgroundColor,
              color,
              padding: "16px",
              borderRadius: "4px",
              border: `1px solid ${borderColor}`,
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Code>
        </Stack>
      );
    }
    return null;
  };

  const loading = isLoading || isLoadingExport;

  // --- Dev panel: snygg lista med attributnamn och värden ---
  const devRows: Array<{ key: string; value: React.ReactNode }> = [
    { key: "id", value: exportData?.id ?? "—" },
    { key: "metadata.name", value: form.values.name || "—" },
    { key: "metadata.status", value: form.values.status || "—" },
    { key: "metadata.disciplineCode", value: form.values.disciplineCode || "—" },
    { key: "metadata.templateDescription", value: form.values.templateDescription || "—" },
    { key: "metadata.subDiscipline", value: form.values.subDiscipline || "—" },
    { key: "metadata.competenceNetwork", value: form.values.competenceNetwork || "—" },
    { key: "metadata.documentManagers.preparedBy", value: form.values.documentManagers?.preparedBy || "—" },
    { key: "metadata.documentManagers.checkedBy", value: form.values.documentManagers?.checkedBy || "—" },
    { key: "metadata.documentManagers.approvedBy", value: form.values.documentManagers?.approvedBy || "—" },
    { key: "metadata.disciplineLeader", value: form.values.disciplineLeader || "—" },
  ];

  return (
    <Box>
      <Modal opened onClose={() => onClose()} title="Publiser sjekkliste" size="lg">
        <Stack>
          {(loading || isErrorExport) && (
            <>
              {loading && <LoadingOverlay visible />}
              {isErrorExport && <Code block>{JSON.stringify(exportError, null, 2)}</Code>}
            </>
          )}

          {/* --- ID tydelig øverst --- */}
          <Paper withBorder p="sm" radius="md">
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>Sjekkliste</Text>
              <Group gap="xs">
                <Code>id</Code>
                <Text fw={700}>{exportData?.id ?? "—"}</Text>
              </Group>
            </Group>
          </Paper>

          <Dropzone
            onDrop={handleDrop}
            accept={{
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              "application/vnd.ms-excel": [".xls"],
            }}
            loading={loading}
            disabled={loading}
            h={200}
            p={10}
          >
            <Text ta="center">Slipp en Excel-fil her eller klikk for å velge</Text>
          </Dropzone>
          {file && <Text>Valgt fil: {file.name}</Text>}

          <Divider label="Metadata" />

          {/* METADATA-FORM */}
          <Stack>
            <TextInput
              label="Navn"
              placeholder="F.eks. Prosjekt X – Elektro sjekkliste"
              required
              {...form.getInputProps("name")}
            />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Status"
                  placeholder="Velg status"
                  data={[
                    { value: "IN_DEVELOPMENT", label: "IN_DEVELOPMENT" },
                    { value: "ACTIVE", label: "ACTIVE" },
                    { value: "REMOVED", label: "REMOVED" },
                  ]}
                  withCheckIcon={false}
                  searchable={false}
                  clearable={false}
                  required
                  {...form.getInputProps("status")}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Disiplinkode"
                  placeholder="F.eks. RIV / RIE"
                  {...form.getInputProps("disciplineCode")}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Underdisiplin"
                  placeholder="F.eks. Automatisering"
                  {...form.getInputProps("subDiscipline")}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Kompetansenettverk"
                  placeholder="F.eks. Energi"
                  {...form.getInputProps("competenceNetwork")}
                />
              </Grid.Col>
            </Grid>

            <Textarea
              label="Malforklaring"
              placeholder="Kort beskrivelse av mal og bruksområde"
              autosize
              minRows={2}
              {...form.getInputProps("templateDescription")}
            />

            <Divider label="Dokumentansvarlige" />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Utarbeidet av (e-post)"
                  placeholder="upn@multiconsult.no"
                  type="email"
                  description="Må være e-post. Brukersjekk gjøres ved publisering."
                  {...form.getInputProps("documentManagers.preparedBy")}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Kontrollert av (e-post)"
                  placeholder="upn@multiconsult.no"
                  type="email"
                  description="Må være e-post. Brukersjekk gjøres ved publisering."
                  {...form.getInputProps("documentManagers.checkedBy")}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Godkjent av (e-post)"
                  placeholder="upn@multiconsult.no"
                  type="email"
                  description="Må være e-post. Brukersjekk gjøres ved publisering."
                  {...form.getInputProps("documentManagers.approvedBy")}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Disiplinleder (e-post)"
                  placeholder="upn@multiconsult.no"
                  type="email"
                  description="Må være e-post. Brukersjekk gjøres ved publisering."
                  {...form.getInputProps("disciplineLeader")}
                />
              </Grid.Col>
            </Grid>
          </Stack>

          <Group justify="space-between">
            <Switch
              label="Simuler publisering (dry-run)"
              checked={dryRun}
              onChange={(event) => setDryRun(event.currentTarget.checked)}
              color="gray"
            />
            <Button onClick={handleSubmit} disabled={!file || loading} color="green">
              Publiser sjekkliste
            </Button>
          </Group>

          {renderValidationResult()}

          {/* --- Dev / integration info --- */}
          <Divider label="Integration / Dev info" />
          <Paper withBorder p="sm" radius="md">
            <Stack gap="xs">
              {devRows.map((r) => (
                <Group key={r.key} gap="xs" wrap="nowrap" align="baseline">
                  <Code>{r.key}</Code>
                  <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.value}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Modal>
    </Box>
  );
};

const PublishChecklist: React.FC<{ checklistNrId: number }> = ({ checklistNrId }) => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <Button onClick={() => setOpened(true)} color="blue" leftSection={<i className="fa fa-plus" />} w={200}>
        Publiser sjekkliste
      </Button>
      {opened && <PublishChecklistModal checklistNrId={checklistNrId} onClose={() => setOpened(false)} />}
    </>
  );
};

export default PublishChecklist;
